// ABOUTME: Main JavaScript for the Homara homepage
// ABOUTME: Contains config data, particle systems, point clouds, navigation, and scroll animations

import {
  createReviewSession,
  fetchApplications,
  fetchApplicationDetail,
  updateApplication,
  clearReviewSession
} from './review-api.js';
import firebaseService from '../../Services/FirebaseService.js';

// ============================================
// CONFIGURATION DATA
// ============================================

const ELLIPSE_POSITIONS = [
  { src: '/assets/homepageData/ellipse-1.svg', left: 80, top: 80 },
  { src: '/assets/homepageData/ellipse-1.svg', left: 93, top: 90 },
  { src: '/assets/homepageData/ellipse-1.svg', left: 93, top: 65 },
  { src: '/assets/homepageData/ellipse-2.svg', left: 88, top: 87 },
  { src: '/assets/homepageData/ellipse-2.svg', left: 67, top: 92 },
  { src: '/assets/homepageData/ellipse-2.svg', left: 87, top: 67 },
  { src: '/assets/homepageData/ellipse-2.svg', left: 78, top: 72 },
  { src: '/assets/homepageData/ellipse-3.svg', left: 75, top: 85 },
  { src: '/assets/homepageData/ellipse-3.svg', left: 85, top: 75 },
  { src: '/assets/homepageData/ellipse-3.svg', left: 96, top: 77 },
  { src: '/assets/homepageData/ellipse-4.svg', left: 71, top: 90 },
  { src: '/assets/homepageData/ellipse-4.svg', left: 83, top: 88 },
  { src: '/assets/homepageData/ellipse-4.svg', left: 92, top: 78 },
  { src: '/assets/homepageData/ellipse-4.svg', left: 82, top: 70 },
  { src: '/assets/homepageData/ellipse-4.svg', left: 92, top: 75 },
  { src: '/assets/homepageData/ellipse-1.svg', left: 77, top: 93 },
];

const RED_CIRCLE_POSITION = { left: 76, top: 83 };

const CAROUSEL_SLIDES = [
  {
    title: 'Community In 3D',
    description: 'We believe that human autonomy matters. Our team uses 3D point cloud models to visualize communities in 3D space. Letting people explore and navigate independently in an effort give people back their agency in an age of feedback loops and engagement.',
    media: '/assets/images/community.webp',
    type: 'image'
  },
  {
    title: 'Our Goal',
    description: 'To find a healthy balance of consumption that fuels creative output instead of dampening it.',
    media: '/assets/videos/FireNOBG.0.webm',
    type: 'video'
  },
  {
    title: 'Human Privacy in the Age of AI',
    description: 'All messages in the Homara Ecosystem have E2E encryption. We will never share data regarding uploads on the platform to companies training AI models.',
    media: '/assets/images/messages.webp',
    type: 'image'
  },
  {
    title: 'Honest Policy & No Ads',
    description: 'The internet was meant to bring us together, not keep us fixated to a screen for the sake of selling our data to advertisers. Homara will never have ads, or try and drive engagement at the expense of your privacy, or our community.',
    media: '/assets/images/fullui.webp',
    type: 'image'
  },
  {
    title: 'Vanity Metrics Have No Place Here',
    description: 'Homara is about connecting and empowering action takers. Using creative design-driven tools we hope to connect artists in a space where authenticity and vulnerability are welcomed. There is hope for a better, healthier internet. And its here. A place where we can create a positive upward spiral together. ',
    media: '/assets/images/fiya.webp',
    type: 'image'
  }
];

// ============================================
// STATE
// ============================================

let particles = [];
let particleStates = [];
let particleAnimationFrame = null;
let particleTime = 0;
let mousePosition = { x: -1000, y: -1000 };
let communityModal = null;
let pricingInfoModal = null;
let passwordModal = null;
let mobileModal = null;
let reviewerModal = null;
let currentSlideIndex = 0;
let isTeamMode = false;

// ============================================
// UTILITIES
// ============================================

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// ============================================
// INITIALIZATION
// ============================================

document.addEventListener('DOMContentLoaded', init);

async function init() {
  createNavigation();
  createLaunchSection();
  createDefinitionSection();
  createPricingSection();
  createValuesCarouselSection();
  createEmailFormSection();
  createFooterSection();
  createScrollIndicator();

  preloadCarouselImages();
  await loadParticles();

  const isLoggedIn = await checkAuthState();

  if (isLoggedIn) {
    // Authenticated user — show everything instantly, particles appear static
    renderParticlesStatic();
    skipIntroAnimations();
  } else {
    // Guest — full animated intro
    startParticleAnimation();
    setupScrollObservers();
  }

  setupEmailForm();
}

// ============================================
// AUTH CHECK (skip animations for logged-in users)
// ============================================

/**
 * Race Firebase onAuthStateChanged against a short timeout.
 * Returns true if the user is authenticated, false otherwise.
 * Firebase caches auth state in IndexedDB so this resolves fast (~50-100ms)
 * for returning users. The timeout ensures first-time visitors are never delayed.
 */
async function checkAuthState() {
  const AUTH_TIMEOUT_MS = 400;

  try {
    await firebaseService.initializeFirebase();
  } catch (err) {
    console.warn('[Homepage] Firebase init failed, running full animations:', err.message);
    return false;
  }

  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      console.log('[Homepage] Auth check timed out — running full animations');
      resolve(false);
    }, AUTH_TIMEOUT_MS);

    firebaseService.onAuthStateChanged((user) => {
      clearTimeout(timeout);
      if (user) {
        console.log('[Homepage] User authenticated — skipping intro animations');
        resolve(true);
      } else {
        console.log('[Homepage] No user — running full animations');
        resolve(false);
      }
    });
  });
}

/**
 * Draw all particles once on the canvas at their base positions.
 * No breathing motion, no mouse interaction, no requestAnimationFrame loop.
 */
function renderParticlesStatic() {
  const canvas = document.getElementById('particle-canvas');
  if (!canvas || particles.length === 0) return;

  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;

  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * dpr;
  canvas.height = rect.height * dpr;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);

  const width = rect.width;
  const height = rect.height;

  particles.forEach((particle) => {
    const x = particle.x * width;
    const y = particle.y * height;
    const size = 3;
    const lightenFactor = 0.30;
    const r = particle.r + (255 - particle.r) * lightenFactor;
    const g = particle.g + (255 - particle.g) * lightenFactor;
    const b = particle.b + (255 - particle.b) * lightenFactor;

    ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
    ctx.beginPath();
    ctx.arc(x, y, size / 2, 0, Math.PI * 2);
    ctx.fill();
  });

  // Redraw on resize so the static particles stay correct
  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => renderParticlesStatic(), 150);
  });
}

/**
 * Skip all intro animations — show everything immediately.
 * - Mark animated sections as visible (no fade-in)
 * - Expand the definition underline fully
 * - Hide the scroll indicator
 */
function skipIntroAnimations() {
  // Immediately reveal sections that normally fade in on scroll
  const definitionSection = document.querySelector('.section-definition');
  if (definitionSection) {
    definitionSection.classList.add('visible');
  }

  // Show definition underline at full width
  const underline = document.querySelector('.definition-underline');
  if (underline) {
    underline.style.transform = 'scaleX(1)';
  }

  // Hide the bounce-arrow scroll indicator
  const scrollIndicator = document.querySelector('.scroll-indicator');
  if (scrollIndicator) {
    scrollIndicator.classList.add('hidden');
  }
}

// ============================================
// NAVIGATION
// ============================================

function createNavigation() {
  const nav = document.createElement('nav');
  nav.className = 'nav';

  nav.innerHTML = `
    <div class="nav-brand">homara</div>
    <div class="nav-links">
      <a href="/" class="nav-link active">Home</a>
      <a href="/build" class="nav-link">Build</a>
      <a href="/apply" class="nav-link">Apply</a>
      <a href="/shop" class="nav-link">Shop</a>
    </div>
  `;

  document.body.appendChild(nav);

  setupNavScrollAnimation();
}

function setupNavScrollAnimation() {
  const navBrand = document.querySelector('.nav-brand');
  const navLinks = document.querySelector('.nav-links');

  if (!navBrand || !navLinks) return;

  // Get the height of the brand element plus the gap
  const brandHeight = navBrand.offsetHeight;
  const navGap = 20; // matches the CSS gap
  const totalOffset = brandHeight + navGap;

  // Scroll threshold for complete transition
  const scrollThreshold = 350;

  function updateNavOnScroll() {
    const scrollY = window.scrollY;

    // Calculate progress (0 to 1) based on scroll position
    const progress = Math.min(scrollY / scrollThreshold, 1);

    // Fade out brand
    navBrand.style.opacity = 1 - progress;

    // Move nav-links up by the same amount as scroll to create illusion
    // At progress=1, links should be at the top (moved up by totalOffset)
    const translateY = -totalOffset * progress;
    navLinks.style.transform = `translateY(${translateY}px)`;
  }

  // Use passive listener for better scroll performance
  window.addEventListener('scroll', updateNavOnScroll, { passive: true });

  // Initial call in case page loads scrolled
  updateNavOnScroll();
}

// ============================================
// SCROLL INDICATOR
// ============================================

function createScrollIndicator() {
  const indicator = document.createElement('div');
  indicator.className = 'scroll-indicator';
  indicator.innerHTML = `
    <svg class="scroll-indicator-arrow" viewBox="0 0 24 24">
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  `;
  document.body.appendChild(indicator);

  // Hide on scroll
  const scrollThreshold = 50;

  function updateIndicatorVisibility() {
    if (window.scrollY > scrollThreshold) {
      indicator.classList.add('hidden');
    } else {
      indicator.classList.remove('hidden');
    }
  }

  window.addEventListener('scroll', updateIndicatorVisibility, { passive: true });
  updateIndicatorVisibility();
}

// ============================================
// COMMUNITY MODAL
// ============================================

function createCommunityModal() {
  const overlay = document.createElement('div');
  overlay.className = 'community-modal-overlay';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) hideCommunityModal();
  });

  const dialog = document.createElement('div');
  dialog.className = 'community-modal-dialog';

  const title = document.createElement('h1');
  title.className = 'community-modal-title';
  title.textContent = 'explore';

  const list = document.createElement('div');
  list.className = 'community-modal-list';
  list.id = 'community-list';
  list.innerHTML = '<span class="community-modal-loading">loading communities...</span>';

  const teamButton = document.createElement('button');
  teamButton.className = 'community-modal-team-button';
  teamButton.id = 'community-modal-team-button';
  teamButton.textContent = 'team';
  teamButton.addEventListener('click', () => {
    if (isTeamMode) {
      // Already in team mode, do nothing
      return;
    }
    hideCommunityModal();
    showPasswordModal();
  });

  dialog.appendChild(title);
  dialog.appendChild(list);
  dialog.appendChild(teamButton);
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  communityModal = overlay;
  return overlay;
}

function showCommunityModal() {
  if (!communityModal) createCommunityModal();
  // Reset team mode each time modal opens
  isTeamMode = false;
  updateTeamButtonState();
  communityModal.classList.add('visible');
  fetchCommunities();
}

function hideCommunityModal() {
  if (communityModal) communityModal.classList.remove('visible');
}

function updateTeamButtonState() {
  const teamButton = document.getElementById('community-modal-team-button');
  if (teamButton) {
    if (isTeamMode) {
      teamButton.textContent = 'viewing all';
      teamButton.classList.add('active');
    } else {
      teamButton.textContent = 'team';
      teamButton.classList.remove('active');
    }
  }
}

// ============================================
// PASSWORD MODAL
// ============================================

function createPasswordModal() {
  const overlay = document.createElement('div');
  overlay.className = 'password-modal-overlay';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) hidePasswordModal();
  });

  const dialog = document.createElement('div');
  dialog.className = 'password-modal-dialog';

  const title = document.createElement('h1');
  title.className = 'password-modal-title';
  title.textContent = 'enter password';

  const input = document.createElement('input');
  input.className = 'password-modal-input';
  input.type = 'password';
  input.placeholder = 'password';
  input.id = 'password-modal-input';

  const errorMsg = document.createElement('span');
  errorMsg.className = 'password-modal-error';
  errorMsg.id = 'password-modal-error';
  errorMsg.textContent = 'incorrect password';

  const submitBtn = document.createElement('button');
  submitBtn.className = 'password-modal-submit';
  submitBtn.textContent = 'enter';

  submitBtn.addEventListener('click', async () => {
    const value = input.value;
    if (!value) return;

    submitBtn.disabled = true;
    submitBtn.textContent = '...';
    errorMsg.classList.remove('visible');

    const result = await createReviewSession(value);

    if (result.success) {
      input.value = '';
      hidePasswordModal();
      showReviewerModal();
    } else {
      errorMsg.classList.add('visible');
      input.value = '';
      input.focus();
    }

    submitBtn.disabled = false;
    submitBtn.textContent = 'enter';
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') submitBtn.click();
  });

  dialog.appendChild(title);
  dialog.appendChild(input);
  dialog.appendChild(errorMsg);
  dialog.appendChild(submitBtn);
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  passwordModal = overlay;
  return overlay;
}

function showPasswordModal() {
  if (!passwordModal) createPasswordModal();
  passwordModal.classList.add('visible');
  // Reset state each time it opens
  const input = document.getElementById('password-modal-input');
  const error = document.getElementById('password-modal-error');
  if (input) { input.value = ''; input.focus(); }
  if (error) error.classList.remove('visible');
}

function hidePasswordModal() {
  if (passwordModal) passwordModal.classList.remove('visible');
}

// ============================================
// REVIEWER MODAL
// ============================================

function createReviewerModal() {
  const overlay = document.createElement('div');
  overlay.className = 'reviewer-modal-overlay';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) hideReviewerModal();
  });

  const dialog = document.createElement('div');
  dialog.className = 'reviewer-modal-dialog';

  const header = document.createElement('div');
  header.className = 'reviewer-modal-header';

  const title = document.createElement('h1');
  title.className = 'reviewer-modal-title';
  title.textContent = 'applications';

  const headerActions = document.createElement('div');
  headerActions.className = 'reviewer-modal-header-actions';

  const refreshBtn = document.createElement('button');
  refreshBtn.className = 'reviewer-modal-refresh';
  refreshBtn.textContent = '\u21BB';
  refreshBtn.title = 'refresh applications';
  refreshBtn.addEventListener('click', loadApplicationList);

  const closeBtn = document.createElement('button');
  closeBtn.className = 'reviewer-modal-close';
  closeBtn.textContent = '\u00D7';
  closeBtn.addEventListener('click', hideReviewerModal);

  const communityBtn = document.createElement('button');
  communityBtn.className = 'reviewer-modal-community-btn';
  communityBtn.textContent = 'full community list';
  communityBtn.addEventListener('click', loadCommunityList);

  headerActions.appendChild(communityBtn);
  headerActions.appendChild(refreshBtn);
  headerActions.appendChild(closeBtn);

  header.appendChild(title);
  header.appendChild(headerActions);

  const body = document.createElement('div');
  body.className = 'reviewer-modal-body';
  body.id = 'reviewer-modal-body';

  dialog.appendChild(header);
  dialog.appendChild(body);
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  reviewerModal = overlay;
  return overlay;
}

function showReviewerModal() {
  if (!reviewerModal) createReviewerModal();
  reviewerModal.classList.add('visible');
  loadApplicationList();
}

function hideReviewerModal() {
  if (reviewerModal) reviewerModal.classList.remove('visible');
  clearReviewSession();
}

function formatDate(isoString) {
  const d = new Date(isoString);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusClass(status) {
  return 'reviewer-status-' + status.toLowerCase().replace(/\s+/g, '-');
}

async function loadApplicationList() {
  const body = document.getElementById('reviewer-modal-body');
  if (!body) return;

  body.innerHTML = '<span class="reviewer-modal-loading">loading applications...</span>';

  const result = await fetchApplications();

  if (!result.success) {
    body.innerHTML = `<span class="reviewer-modal-loading reviewer-modal-error">${result.error || 'failed to load applications'}</span>`;
    return;
  }

  const apps = (result.applications || []).filter(
    (app) => app.status !== 'Accepted' && app.status !== 'Rejected'
  );

  if (apps.length === 0) {
    body.innerHTML = '<span class="reviewer-modal-loading">no applications yet</span>';
    return;
  }

  // Group by city
  const grouped = {};
  apps.forEach((app) => {
    if (!grouped[app.city]) grouped[app.city] = [];
    grouped[app.city].push(app);
  });

  // Sort cities alphabetically
  const cities = Object.keys(grouped).sort();

  body.innerHTML = '';

  cities.forEach((city) => {
    const section = document.createElement('div');
    section.className = 'reviewer-city-group';

    const heading = document.createElement('h2');
    heading.className = 'reviewer-city-heading';
    heading.textContent = city;
    section.appendChild(heading);

    grouped[city].forEach((app) => {
      const row = document.createElement('div');
      row.className = 'reviewer-app-row';

      row.innerHTML = `
        <span class="reviewer-app-name">${escapeHtml(app.applicantName)}</span>
        <span class="reviewer-app-date">${formatDate(app.createdAt)}</span>
        <span class="reviewer-app-status ${statusClass(app.status)}">${app.status}</span>
      `;

      const viewBtn = document.createElement('button');
      viewBtn.className = 'reviewer-app-view';
      viewBtn.textContent = 'view application';
      viewBtn.addEventListener('click', () => loadApplicationDetail(app.id));
      row.appendChild(viewBtn);

      section.appendChild(row);
    });

    body.appendChild(section);
  });
}

async function loadCommunityList() {
  const body = document.getElementById('reviewer-modal-body');
  if (!body) return;

  body.innerHTML = '<span class="reviewer-modal-loading">loading communities...</span>';

  try {
    const response = await fetch('/api/communities');
    const data = await response.json();

    if (!data.success || !data.communities || data.communities.length === 0) {
      body.innerHTML = '<span class="reviewer-modal-loading">no communities available</span>';
      return;
    }

    body.innerHTML = '';

    const backBtn = document.createElement('button');
    backBtn.className = 'reviewer-detail-back';
    backBtn.textContent = '\u2190 back to applications';
    backBtn.addEventListener('click', loadApplicationList);
    body.appendChild(backBtn);

    const heading = document.createElement('h2');
    heading.className = 'reviewer-city-heading';
    heading.textContent = 'all communities';
    body.appendChild(heading);

    data.communities.forEach((community) => {
      if (!community.slug) return;
      const btn = document.createElement('button');
      btn.className = 'community-modal-button';
      btn.textContent = community.communityName;
      btn.addEventListener('click', () => {
        const viewerUrl = `/${community.slug}`;
        window.open(viewerUrl, '_blank');
      });
      body.appendChild(btn);
    });
  } catch (error) {
    body.innerHTML = '<span class="reviewer-modal-loading reviewer-modal-error">failed to load communities</span>';
  }
}

const VALID_STATUSES = ['Pending', 'In Review', 'Accepted', 'Rejected'];

async function loadApplicationDetail(id) {
  const body = document.getElementById('reviewer-modal-body');
  if (!body) return;

  body.innerHTML = '<span class="reviewer-modal-loading">loading application...</span>';

  const result = await fetchApplicationDetail(id);

  if (!result.success) {
    body.innerHTML = `<span class="reviewer-modal-loading reviewer-modal-error">${result.error || 'failed to load application'}</span>`;
    return;
  }

  renderDetailPanel(result.application);
}

function renderDetailPanel(app) {
  const body = document.getElementById('reviewer-modal-body');
  if (!body) return;

  body.innerHTML = '';

  // Back button
  const backBtn = document.createElement('button');
  backBtn.className = 'reviewer-detail-back';
  backBtn.textContent = '\u2190 back to list';
  backBtn.addEventListener('click', loadApplicationList);
  body.appendChild(backBtn);

  // Header with name and submission ID
  const header = document.createElement('div');
  header.className = 'reviewer-detail-header';
  header.innerHTML = `
    <h2 class="reviewer-detail-name">${escapeHtml(app.applicantName)}</h2>
    <span class="reviewer-detail-id">${escapeHtml(app.submissionId)}</span>
  `;
  body.appendChild(header);

  // Meta row: email, age, city, date
  const meta = document.createElement('div');
  meta.className = 'reviewer-detail-meta';
  meta.innerHTML = `
    <span>${escapeHtml(app.applicantEmail)}</span>
    <span>age ${app.applicantAge}</span>
    <span>${escapeHtml(app.city)}</span>
    <span>${formatDate(app.createdAt)}</span>
  `;
  body.appendChild(meta);

  // Status selector
  const statusRow = document.createElement('div');
  statusRow.className = 'reviewer-detail-status-row';

  const statusLabel = document.createElement('span');
  statusLabel.className = 'reviewer-detail-label';
  statusLabel.textContent = 'status';
  statusRow.appendChild(statusLabel);

  const statusSelect = document.createElement('select');
  statusSelect.className = 'reviewer-detail-status-select';
  VALID_STATUSES.forEach((s) => {
    const opt = document.createElement('option');
    opt.value = s;
    opt.textContent = s;
    if (s === app.status) opt.selected = true;
    statusSelect.appendChild(opt);
  });
  statusSelect.addEventListener('change', async () => {
    statusSelect.disabled = true;
    const res = await updateApplication(app.id, { status: statusSelect.value });
    statusSelect.disabled = false;
    if (!res.success) {
      statusSelect.value = app.status;
    } else {
      app.status = res.application.status;
    }
  });
  statusRow.appendChild(statusSelect);
  body.appendChild(statusRow);

  // Passion response
  body.appendChild(createReadOnlySection('what are you passionate about?', app.passionResponse));

  // Journey response
  body.appendChild(createReadOnlySection('tell us about your journey', app.journeyResponse));

  // Media attachments
  if (app.mediaAttachments && app.mediaAttachments.length > 0) {
    const mediaSection = document.createElement('div');
    mediaSection.className = 'reviewer-detail-section';
    const mediaLabel = document.createElement('h3');
    mediaLabel.className = 'reviewer-detail-label';
    mediaLabel.textContent = 'media attachments';
    mediaSection.appendChild(mediaLabel);

    const mediaList = document.createElement('div');
    mediaList.className = 'reviewer-detail-media-list';
    app.mediaAttachments.forEach((url) => {
      const link = document.createElement('a');
      link.className = 'reviewer-detail-media-link';
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = url.split('/').pop();
      mediaList.appendChild(link);
    });
    mediaSection.appendChild(mediaList);
    body.appendChild(mediaSection);
  }

  // Voice note
  if (app.voiceNote) {
    const voiceSection = document.createElement('div');
    voiceSection.className = 'reviewer-detail-section';
    const voiceLabel = document.createElement('h3');
    voiceLabel.className = 'reviewer-detail-label';
    voiceLabel.textContent = 'voice note';
    voiceSection.appendChild(voiceLabel);

    const audio = document.createElement('audio');
    audio.className = 'reviewer-detail-audio';
    audio.controls = true;
    audio.src = app.voiceNote;
    voiceSection.appendChild(audio);
    body.appendChild(voiceSection);
  }

  // Voice note text fallback
  if (app.voiceNoteText) {
    body.appendChild(createReadOnlySection('voice note (typed)', app.voiceNoteText));
  }

  // Internal notes (editable)
  body.appendChild(createEditableSection('internal notes', app.internalNotes, async (value) => {
    return updateApplication(app.id, { internalNotes: value });
  }));

  // Decision reason (editable)
  body.appendChild(createEditableSection('decision message', app.decisionReason, async (value) => {
    return updateApplication(app.id, { decisionReason: value });
  }));
}

function createReadOnlySection(label, text) {
  const section = document.createElement('div');
  section.className = 'reviewer-detail-section';

  const heading = document.createElement('h3');
  heading.className = 'reviewer-detail-label';
  heading.textContent = label;
  section.appendChild(heading);

  const content = document.createElement('p');
  content.className = 'reviewer-detail-text';
  content.textContent = text || '(no response)';
  section.appendChild(content);

  return section;
}

function createEditableSection(label, initialValue, onSave) {
  const section = document.createElement('div');
  section.className = 'reviewer-detail-section';

  const heading = document.createElement('h3');
  heading.className = 'reviewer-detail-label';
  heading.textContent = label;
  section.appendChild(heading);

  const textarea = document.createElement('textarea');
  textarea.className = 'reviewer-detail-textarea';
  textarea.value = initialValue || '';
  textarea.rows = 3;
  section.appendChild(textarea);

  const saveBtn = document.createElement('button');
  saveBtn.className = 'reviewer-detail-save';
  saveBtn.textContent = 'save';
  saveBtn.addEventListener('click', async () => {
    saveBtn.disabled = true;
    saveBtn.textContent = 'saving...';
    const res = await onSave(textarea.value);
    saveBtn.disabled = false;
    saveBtn.textContent = res.success ? 'saved' : 'failed';
    setTimeout(() => { saveBtn.textContent = 'save'; }, 1500);
  });
  section.appendChild(saveBtn);

  return section;
}

async function fetchCommunities() {
  const listEl = document.getElementById('community-list');

  try {
    const response = await fetch('/api/communities');
    const data = await response.json();

    if (!data.success || !data.communities || data.communities.length === 0) {
      listEl.innerHTML = '<span class="community-modal-loading">no communities available</span>';
      return;
    }

    // Filter communities based on team mode
    const communities = isTeamMode
      ? data.communities
      : data.communities.filter(community => community.isPublic === true);

    if (communities.length === 0) {
      listEl.innerHTML = '<span class="community-modal-loading">no communities available</span>';
      return;
    }

    listEl.innerHTML = '';

    communities.forEach(community => {
      if (!community.slug) return;
      const button = document.createElement('button');
      button.className = 'community-modal-button';
      button.textContent = community.communityName;
      button.addEventListener('click', () => {
        // Both dev and production use the same path format
        // Dev: Vite rewrites /<slug> to /viewer.html?community=<slug>
        // Prod: Firebase rewrites /<slug> to /viewer.html (UrlManager parses path)
        const viewerUrl = `/${community.slug}`;
        window.open(viewerUrl, '_blank');
        hideCommunityModal();
      });
      listEl.appendChild(button);
    });

  } catch (error) {
    console.error('Failed to fetch communities:', error);
    listEl.innerHTML = `
      <span class="community-modal-loading">failed to load communities</span>
      <span class="community-modal-error">${error.message}</span>
    `;
  }
}

// ============================================
// PRICING INFO MODAL
// ============================================

function createPricingInfoModal() {
  const overlay = document.createElement('div');
  overlay.className = 'pricing-info-modal-overlay';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) hidePricingInfoModal();
  });

  const dialog = document.createElement('div');
  dialog.className = 'pricing-info-modal-dialog';

  const message = document.createElement('p');
  message.className = 'pricing-info-modal-message';
  message.textContent = 'Get more info on our build page';

  const link = document.createElement('a');
  link.className = 'pricing-info-modal-link';
  link.href = '/build';
  link.textContent = 'Click here';

  dialog.appendChild(message);
  dialog.appendChild(link);
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  pricingInfoModal = overlay;
  return overlay;
}

function showPricingInfoModal() {
  if (!pricingInfoModal) createPricingInfoModal();
  pricingInfoModal.classList.add('visible');
}

function hidePricingInfoModal() {
  if (pricingInfoModal) pricingInfoModal.classList.remove('visible');
}

// ============================================
// MOBILE MODAL
// ============================================

function isMobile() {
  return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    || window.innerWidth < 768;
}

function createMobileModal() {
  const overlay = document.createElement('div');
  overlay.className = 'mobile-modal-overlay';
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) hideMobileModal();
  });

  const dialog = document.createElement('div');
  dialog.className = 'mobile-modal-dialog';

  const message = document.createElement('p');
  message.className = 'mobile-modal-message';
  message.textContent = 'Sorry, you can\'t launch Homara on mobile devices!';

  const sub = document.createElement('p');
  sub.className = 'mobile-modal-sub';
  sub.textContent = 'Please visit us on a laptop or desktop for the full experience.';

  const closeBtn = document.createElement('button');
  closeBtn.className = 'mobile-modal-close';
  closeBtn.textContent = 'got it';
  closeBtn.addEventListener('click', hideMobileModal);

  dialog.appendChild(message);
  dialog.appendChild(sub);
  dialog.appendChild(closeBtn);
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);

  mobileModal = overlay;
  return overlay;
}

function showMobileModal() {
  if (!mobileModal) createMobileModal();
  mobileModal.classList.add('visible');
}

function hideMobileModal() {
  if (mobileModal) mobileModal.classList.remove('visible');
}

// ============================================
// LAUNCH SECTION
// ============================================

function createLaunchSection() {
  const section = document.createElement('div');
  section.className = 'section-launch';
  section.id = 'launch-section';

  // Particle canvas
  const canvas = document.createElement('canvas');
  canvas.className = 'particle-canvas';
  canvas.id = 'particle-canvas';
  section.appendChild(canvas);

  // Floating ellipses
  ELLIPSE_POSITIONS.forEach((figure, index) => {
    const div = document.createElement('div');
    div.className = 'floating-figure';
    div.style.left = `${figure.left}%`;
    div.style.top = `${figure.top}%`;

    const img = document.createElement('img');
    img.src = figure.src;
    img.alt = `Floating figure ${index + 1}`;
    img.width = 150;
    img.height = 150;
    div.appendChild(img);

    section.appendChild(div);
  });

  // Launch button
  const button = document.createElement('button');
  button.className = 'launch-button';
  button.textContent = 'launch';
  button.style.left = `${RED_CIRCLE_POSITION.left}%`;
  button.style.top = `${RED_CIRCLE_POSITION.top}%`;
  button.addEventListener('click', () => {
    if (isMobile()) {
      showMobileModal();
    } else {
      showCommunityModal();
    }
  });
  section.appendChild(button);

  document.body.appendChild(section);
}

// ============================================
// DEFINITION SECTION
// ============================================

function createDefinitionSection() {
  const section = document.createElement('div');
  section.className = 'section-definition';
  section.id = 'definition-section';

  const container = document.createElement('div');
  container.className = 'definition-container';

  container.innerHTML = `
    <div class="definition-content">
      <h1 class="definition-title"><span class="definition-word">Homara<span class="definition-underline"></span></span> <span class="definition-parens">(</span><span class="definition-type">noun)</span></h1>
      <p class="definition-pronunciation">hom-arah</p>
      <div class="definition-entries">
        <p class="definition-entry"><span class="definition-number">1.</span> a digital civilization that fosters authentic positive growth for oneself and others through love and intention</p>
        <p class="definition-entry"><span class="definition-number">2.</span> an authentically driven human-centric state of being</p>
      </div>
    </div>
    <img class="definition-logo" src="/assets/images/BlackLogo.png" alt="Homara" />
  `;

  section.appendChild(container);
  document.body.appendChild(section);

  setupDefinitionUnderlineAnimation();
}

function setupDefinitionUnderlineAnimation() {
  const definitionSection = document.getElementById('definition-section');
  const underline = document.querySelector('.definition-underline');

  if (!definitionSection || !underline) return;

  function updateUnderlineOnScroll() {
    const sectionTop = definitionSection.getBoundingClientRect().top;
    const windowHeight = window.innerHeight;

    // Start fading in when the section enters the viewport
    // Threshold: when section top is at 80% of window height (entering from bottom)
    const fadeStartThreshold = windowHeight * 0.8;
    const fadeEndThreshold = windowHeight * 0.3;

    if (sectionTop <= fadeStartThreshold && sectionTop >= fadeEndThreshold) {
      // Calculate progress as section moves from fadeStart to fadeEnd
      const progress = 1 - (sectionTop - fadeEndThreshold) / (fadeStartThreshold - fadeEndThreshold);
      underline.style.transform = `scaleX(${progress})`;
    } else if (sectionTop < fadeEndThreshold) {
      // Fully visible
      underline.style.transform = 'scaleX(1)';
    } else {
      // Not yet visible
      underline.style.transform = 'scaleX(0)';
    }
  }

  window.addEventListener('scroll', updateUnderlineOnScroll, { passive: true });
  updateUnderlineOnScroll();
}

// ============================================
// PRICING SECTION
// ============================================

function createPricingSection() {
  const section = document.createElement('div');
  section.className = 'section-pricing';
  section.id = 'pricing-section';

  // Beta box
  const betaBox = document.createElement('div');
  betaBox.className = 'pricing-box';

  const betaContent = document.createElement('div');
  betaContent.className = 'pricing-box-content';

  const betaText = document.createElement('p');
  betaText.className = 'pricing-box-text';
  betaText.innerHTML = '<strong>Homara Beta</strong> : our first community is launching soon! please join the newsletter for updates!';

  betaContent.appendChild(betaText);

  const betaLogo = document.createElement('img');
  betaLogo.className = 'pricing-box-logo';
  betaLogo.src = '/assets/homepageData/evolution-logo.png';
  betaLogo.alt = 'Evolution';

  betaBox.appendChild(betaContent);
  betaBox.appendChild(betaLogo);

  // Full Release box
  const releaseBox = document.createElement('div');
  releaseBox.className = 'pricing-box';

  const releaseContent = document.createElement('div');
  releaseContent.className = 'pricing-box-content';

  const releaseText = document.createElement('p');
  releaseText.className = 'pricing-box-text';
  releaseText.innerHTML = '<strong>Homara Full Release</strong> : once we release the program, anyone will be able to build a community and invite their friends. <span class="pricing-see-more">See more...</span>';

  releaseContent.appendChild(releaseText);

  // Add click event to "See more..." span
  const seeMoreSpan = releaseText.querySelector('.pricing-see-more');
  if (seeMoreSpan) {
    seeMoreSpan.addEventListener('click', () => {
      showPricingInfoModal();
    });
  }

  const priceButtons = document.createElement('div');
  priceButtons.className = 'pricing-buttons';

  const yearlyBtn = document.createElement('span');
  yearlyBtn.className = 'pricing-button';
  yearlyBtn.textContent = '$50 / year';

  const monthlyBtn = document.createElement('span');
  monthlyBtn.className = 'pricing-button';
  monthlyBtn.textContent = '$7 / month';

  priceButtons.appendChild(yearlyBtn);
  priceButtons.appendChild(monthlyBtn);

  releaseBox.appendChild(releaseContent);
  releaseBox.appendChild(priceButtons);

  section.appendChild(betaBox);
  section.appendChild(releaseBox);
  document.body.appendChild(section);
}

// ============================================
// VALUES CAROUSEL SECTION
// ============================================

function createValuesCarouselSection() {
  const section = document.createElement('div');
  section.className = 'section-values-carousel';
  section.id = 'values-carousel-section';

  // Media frame (standalone, no arrows next to it)
  const mediaFrame = document.createElement('div');
  mediaFrame.className = 'carousel-image-frame';
  mediaFrame.id = 'carousel-media-frame';

  // Create initial media element based on first slide type
  const firstSlide = CAROUSEL_SLIDES[0];
  let mediaElement;

  if (firstSlide.type === 'video') {
    mediaElement = document.createElement('video');
    mediaElement.className = 'carousel-image';
    mediaElement.id = 'carousel-media';
    mediaElement.dataset.src = firstSlide.media; // Use data-src for lazy loading
    mediaElement.preload = 'none'; // Don't preload
    mediaElement.autoplay = true;
    mediaElement.loop = true;
    mediaElement.muted = true;
    mediaElement.playsInline = true;
    mediaFrame.classList.add('has-video');
    mediaFrame.style.minHeight = '400px';
  } else {
    mediaElement = document.createElement('img');
    mediaElement.className = 'carousel-image';
    mediaElement.id = 'carousel-media';
    mediaElement.src = firstSlide.media;
    mediaElement.alt = firstSlide.title;
    mediaFrame.classList.remove('has-video');

    // Set min-height dynamically based on image dimensions
    mediaElement.onload = function() {
      const maxWidth = 650; // matches max-width of frame
      const scaledHeight = (this.naturalHeight / this.naturalWidth) * Math.min(this.naturalWidth, maxWidth);
      mediaFrame.style.minHeight = `${scaledHeight}px`;
    };
  }

  mediaFrame.appendChild(mediaElement);

  // Text container with arrows on sides
  const textContainer = document.createElement('div');
  textContainer.className = 'carousel-text-container';

  // Left arrow
  const leftArrow = document.createElement('button');
  leftArrow.className = 'carousel-arrow carousel-arrow-left';
  leftArrow.innerHTML = '&#x2039;';
  leftArrow.setAttribute('aria-label', 'Previous slide');
  leftArrow.addEventListener('click', () => navigateCarousel(-1));

  // Right arrow
  const rightArrow = document.createElement('button');
  rightArrow.className = 'carousel-arrow carousel-arrow-right';
  rightArrow.innerHTML = '&#x203a;';
  rightArrow.setAttribute('aria-label', 'Next slide');
  rightArrow.addEventListener('click', () => navigateCarousel(1));

  // Text content
  const textContent = document.createElement('div');
  textContent.className = 'carousel-text-content';

  const title = document.createElement('h3');
  title.className = 'carousel-title';
  title.id = 'carousel-title';
  title.textContent = CAROUSEL_SLIDES[0].title;

  const description = document.createElement('p');
  description.className = 'carousel-description';
  description.id = 'carousel-description';
  description.textContent = CAROUSEL_SLIDES[0].description;

  textContent.appendChild(title);
  textContent.appendChild(description);

  textContainer.appendChild(leftArrow);
  textContainer.appendChild(textContent);
  textContainer.appendChild(rightArrow);

  section.appendChild(mediaFrame);
  section.appendChild(textContainer);
  document.body.appendChild(section);
}

function navigateCarousel(direction) {
  currentSlideIndex += direction;

  // Wrap around
  if (currentSlideIndex < 0) {
    currentSlideIndex = CAROUSEL_SLIDES.length - 1;
  } else if (currentSlideIndex >= CAROUSEL_SLIDES.length) {
    currentSlideIndex = 0;
  }

  const slide = CAROUSEL_SLIDES[currentSlideIndex];

  // Batch all DOM updates in a single animation frame for synchronized rendering
  requestAnimationFrame(() => {
    const mediaFrame = document.getElementById('carousel-media-frame');
    const title = document.getElementById('carousel-title');
    const description = document.getElementById('carousel-description');

    if (mediaFrame) {
      // Remove old media element
      const oldMedia = document.getElementById('carousel-media');
      if (oldMedia) {
        oldMedia.remove();
      }

      // Create new media element based on slide type
      let newMedia;
      if (slide.type === 'video') {
        newMedia = document.createElement('video');
        newMedia.className = 'carousel-image';
        newMedia.id = 'carousel-media';
        newMedia.dataset.src = slide.media; // Use data-src for lazy loading
        newMedia.preload = 'none'; // Don't preload
        newMedia.autoplay = true;
        newMedia.loop = true;
        newMedia.muted = true;
        newMedia.playsInline = true;
        mediaFrame.classList.add('has-video');
        mediaFrame.style.minHeight = '400px';

        // Lazy load the video when it becomes visible
        lazyLoadCarouselVideo(newMedia);
      } else {
        newMedia = document.createElement('img');
        newMedia.className = 'carousel-image';
        newMedia.id = 'carousel-media';
        newMedia.src = slide.media;
        newMedia.alt = slide.title;
        mediaFrame.classList.remove('has-video');

        // Set min-height dynamically based on image dimensions
        newMedia.onload = function() {
          const maxWidth = 650; // matches max-width of frame
          const scaledHeight = (this.naturalHeight / this.naturalWidth) * Math.min(this.naturalWidth, maxWidth);
          mediaFrame.style.minHeight = `${scaledHeight}px`;
        };
      }

      mediaFrame.appendChild(newMedia);
    }

    if (title) {
      title.textContent = slide.title;
    }
    if (description) {
      description.textContent = slide.description;
    }
  });
}

// Lazy load carousel video when it becomes visible
function lazyLoadCarouselVideo(videoElement) {
  if (!videoElement.src && videoElement.dataset.src) {
    videoElement.src = videoElement.dataset.src;
    videoElement.load();
  }
}

function preloadCarouselImages() {
  CAROUSEL_SLIDES.forEach(slide => {
    if (slide.type === 'image') {
      const img = new Image();
      img.src = slide.media;
    }
    // Videos are lazy loaded when navigated to, so we don't preload them
  });
}

// ============================================
// EMAIL FORM SECTION
// ============================================

function createEmailFormSection() {
  const section = document.createElement('div');
  section.className = 'section-email-form';
  section.id = 'email-form-section';

  const container = document.createElement('div');
  container.className = 'email-form-container';

  const title = document.createElement('h2');
  title.className = 'email-form-title';
  title.textContent = 'Coming Soon.';

  const description = document.createElement('p');
  description.className = 'email-form-description';
  description.textContent = 'join our email list to learn more about the project and get notified when we launch!';

  const form = document.createElement('form');
  form.className = 'email-form';
  form.id = 'email-form';

  const inputWrapper = document.createElement('div');
  inputWrapper.className = 'email-form-input-wrapper';

  const input = document.createElement('input');
  input.type = 'email';
  input.className = 'email-form-input';
  input.id = 'email-input';
  input.placeholder = 'enter your email';
  input.required = true;

  const button = document.createElement('button');
  button.type = 'submit';
  button.className = 'email-form-button';
  button.id = 'submit-btn';
  button.textContent = 'submit';

  inputWrapper.appendChild(input);
  inputWrapper.appendChild(button);
  form.appendChild(inputWrapper);

  const message = document.createElement('div');
  message.className = 'email-form-message';
  message.id = 'submission-message';

  container.appendChild(title);
  container.appendChild(description);
  container.appendChild(form);
  container.appendChild(message);
  section.appendChild(container);

  document.body.appendChild(section);
}

// ============================================
// FOOTER SECTION
// ============================================

function createFooterSection() {
  const footer = document.createElement('footer');
  footer.className = 'section-footer';
  footer.id = 'footer-section';

  footer.innerHTML = `
    <div class="footer-content">
      <p class="footer-brand">homara</p>
      <div class="footer-columns">
        <nav class="footer-links">
          <a href="/newsletter" class="footer-link">newsletter</a>
          <a href="/journey" class="footer-link">our journey</a>
          <a href="/team" class="footer-link">our team</a>
        </nav>
        <nav class="footer-links">
          <a href="/guidelines" class="footer-link">community guidelines</a>
          <a href="/contact" class="footer-link">contact and help</a>
          <a href="https://homara.org" class="footer-link" target="_blank">homara.org nonprofit</a>
        </nav>
      </div>
    </div>
  `;

  document.body.appendChild(footer);
}

// ============================================
// DATA LOADING
// ============================================

async function loadParticles() {
  try {
    const response = await fetch('/assets/homepageData/particles.json');
    particles = await response.json();

    // Initialize particle states
    particleStates = particles.map(() => ({
      displaceX: 0,
      displaceY: 0,
      triggered: false,
    }));
  } catch (err) {
    console.error('Failed to load particles:', err);
  }
}

// ============================================
// PARTICLE SYSTEM ANIMATION
// ============================================

function startParticleAnimation() {
  const canvas = document.getElementById('particle-canvas');
  if (!canvas || particles.length === 0) return;

  const ctx = canvas.getContext('2d');
  const dpr = window.devicePixelRatio || 1;

  function updateSize() {
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
  }

  updateSize();

  // Debounced resize handler for better performance
  let resizeTimeout;
  function handleResize() {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(updateSize, 150);
  }

  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', () => {
    setTimeout(updateSize, 100);
  });

  canvas.addEventListener('mousemove', (e) => {
    const rect = canvas.getBoundingClientRect();
    mousePosition.x = e.clientX - rect.left;
    mousePosition.y = e.clientY - rect.top;
  });

  function animate() {
    particleTime += 0.05;

    const width = canvas.width / dpr;
    const height = canvas.height / dpr;

    ctx.clearRect(0, 0, width, height);

    const mouseX = mousePosition.x;
    const mouseY = mousePosition.y;
    const interactionRadius = 20;
    const maxPushStrength = 20;
    const pushSpeed = 0.15;
    const returnSpeed = 0.05;

    particles.forEach((particle, index) => {
      const startX = particle.x * width;
      const startY = particle.y * height;

      const phaseX = particleTime + (index * 0.1);
      const phaseY = particleTime + (index * 0.15);

      const breathMoveY = Math.sin(phaseY) * 2.0;
      const breathMoveX = Math.sin(phaseX) * 1.0;

      const state = particleStates[index];
      const currentX = startX + breathMoveX;
      const currentY = startY + breathMoveY;

      const dx = currentX - mouseX;
      const dy = currentY - mouseY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < interactionRadius) {
        const angle = Math.atan2(dy, dx);
        const targetDisplaceX = Math.cos(angle) * maxPushStrength;
        const targetDisplaceY = Math.sin(angle) * maxPushStrength;

        state.displaceX += (targetDisplaceX - state.displaceX) * pushSpeed;
        state.displaceY += (targetDisplaceY - state.displaceY) * pushSpeed;
        state.triggered = true;
      } else if (state.triggered) {
        state.displaceX *= (1 - returnSpeed);
        state.displaceY *= (1 - returnSpeed);

        if (Math.abs(state.displaceX) < 0.1 && Math.abs(state.displaceY) < 0.1) {
          state.displaceX = 0;
          state.displaceY = 0;
          state.triggered = false;
        }
      }

      const x = currentX + state.displaceX;
      const y = currentY + state.displaceY;

      const size = 3;
      const lightenFactor = 0.30;
      const r = particle.r + (255 - particle.r) * lightenFactor;
      const g = particle.g + (255 - particle.g) * lightenFactor;
      const b = particle.b + (255 - particle.b) * lightenFactor;

      ctx.fillStyle = `rgb(${r}, ${g}, ${b})`;
      ctx.beginPath();
      ctx.arc(x, y, size / 2, 0, Math.PI * 2);
      ctx.fill();
    });

    particleAnimationFrame = requestAnimationFrame(animate);
  }

  animate();
}

// ============================================
// SCROLL ANIMATIONS (Intersection Observer)
// ============================================

function setupScrollObservers() {
  const observerOptions = {
    root: null,
    rootMargin: '-100px',
    threshold: 0.1
  };

  const sectionObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        sectionObserver.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // Observe the definition section
  const definitionSection = document.querySelector('.section-definition');
  if (definitionSection) {
    sectionObserver.observe(definitionSection);
  }
}

// ============================================
// EMAIL FORM SUBMISSION
// ============================================

function setupEmailForm() {
  const emailForm = document.getElementById('email-form');
  const emailInput = document.getElementById('email-input');
  const submitBtn = document.getElementById('submit-btn');
  const submissionMessage = document.getElementById('submission-message');

  if (!emailForm || !emailInput || !submitBtn || !submissionMessage) {
    console.error('Email form elements not found');
    return;
  }

  emailForm.addEventListener('submit', async function(e) {
    e.preventDefault();

    const email = emailInput.value.trim();

    if (!email) {
      return;
    }

    // Disable the button during submission
    submitBtn.disabled = true;
    submitBtn.textContent = 'submitting...';

    try {
      // Submit to Formspree
      const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xgvavkwq';

      const formData = new FormData();
      formData.append('email', email);
      formData.append('submitDate', new Date().toISOString());

      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to submit to Formspree');
      }

      // Clear the input
      emailInput.value = '';

      // Show success message
      submissionMessage.textContent = 'submitted! thanks!';
      submissionMessage.className = 'email-form-message show';

      // Reset the message after 3 seconds
      setTimeout(function() {
        submissionMessage.className = 'email-form-message';
      }, 3000);
    } catch (error) {
      // Show error message
      submissionMessage.textContent = 'error submitting. please try again.';
      submissionMessage.className = 'email-form-message show error';

      // Reset the message after 3 seconds
      setTimeout(function() {
        submissionMessage.className = 'email-form-message';
      }, 3000);

      console.error('Error submitting email:', error);
    } finally {
      // Re-enable the button
      submitBtn.disabled = false;
      submitBtn.textContent = 'submit';
    }
  });
}